"use client";

import { ArrowRight, ChevronDown, Mail, Sparkles } from "lucide-react";

import { Link } from "@/src/lib/next-compat";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import B2BHeroHubDiagram from "@/components/b2b/B2BHeroHubDiagram";
import B2BHowItWorks from "@/components/b2b/B2BHowItWorks";
import Footer from "@/components/landing page/Footer";
import Navbar from "@/components/landing page/Navbar";
import { useState, type ReactNode } from "react";

const CONTACT_MAIL = process.env.NEXT_PUBLIC_B2B_CONTACT_EMAIL ?? "team@atomcamp.com";

const ease = [0.22, 1, 0.36, 1] as [number, number, number, number];

const heroContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.11, delayChildren: 0.08 },
  },
};

const heroItemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease } },
};


const VALUE_PROPS = [
  {
    title: "Built around your subscription",
    body: "Your org takes a platform subscription; we structure seats, access, and renewals the way your procurement and L&D teams need.",
  },
  {
    title: "We build the course for you",
    body: "In return, inaraX produces a course aligned to your goals and audience—not a one-off download, but a living program your people actually take.",
  },
  {
    title: "Leaders see the whole picture",
    body: "Program owners and people managers get a dashboard: who has started, how far they are, and how they score on checkpoints and quizzes.",
  },
  {
    title: "Learners, not just licenses",
    body: "End users get lessons, placement where it applies, and quizzes with feedback; you keep visibility without running a second LMS on the side.",
  },
] as const;

const FAQS = [
  {
    q: "What do we get when we subscribe as an organization?",
    a: "A subscription to the inaraX platform, a course built for your org’s goals, seats for your users, and an admin view so you can track engagement, completion, and quiz results—not just a static content dump.",
  },
  {
    q: "Who produces the course, and can we influence it?",
    a: "inaraX generates and hosts the program you subscribe to, shaped around the outcomes and audience you need. We align on scope and stakeholders before your users go live.",
  },
  {
    q: "What can our admins see in the dashboard?",
    a: "At a high level: participation, progress through the course, and performance on quizzes and checks—so you can support teams and report upward without guesswork.",
  },
] as const;

const mailHref = `mailto:${CONTACT_MAIL}?subject=Enterprise%20inquiry%20%E2%80%94%20inaraX`;

function Section({
  id,
  eyebrow,
  children,
  className = "",
  innerClass = "max-w-5xl",
  padding = "default",
}: {
  id?: string;
  eyebrow?: string;
  children: ReactNode;
  className?: string;
  innerClass?: string;
  /** "tight" = less vertical space (compact sections) */
  padding?: "default" | "tight";
}) {
  const py =
    padding === "tight" ? "py-6 md:py-7 lg:py-8" : "py-16 md:py-20 lg:py-24";
  return (
    <section id={id} className={["w-full", className].filter(Boolean).join(" ")}>
      <div
        className={["mx-auto w-full px-4 md:px-6", py, innerClass || "max-w-5xl"].filter(Boolean).join(" ")}
      >
        {eyebrow ? (
          <p className="text-center text-sm font-semibold tracking-wide text-stone-500">{eyebrow}</p>
        ) : null}
        {children}
      </div>
    </section>
  );
}

function FaqItem({ q, a }: (typeof FAQS)[number]) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-stone-200 last:border-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-4 py-4 text-left text-base font-bold text-stone-900 transition hover:text-stone-700"
        aria-expanded={open}
      >
        {q}
        <ChevronDown
          className={["h-5 w-5 shrink-0 text-stone-400 transition-transform", open ? "rotate-180" : ""].join(" ")}
        />
      </button>
      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease }}
            className="overflow-hidden"
          >
            <p className="pb-5 pr-2 text-sm leading-relaxed text-stone-600 md:text-base">{a}</p>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export default function B2BPage() {
  const reduce = useReducedMotion();
  const heroContainer = reduce
    ? { hidden: { opacity: 1 }, visible: { opacity: 1, transition: { duration: 0, staggerChildren: 0 } } }
    : heroContainerVariants;
  const heroItem = reduce
    ? { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0, transition: { duration: 0 } } }
    : heroItemVariants;

  return (
    <main className="b2b-page relative flex min-h-screen flex-col overflow-x-hidden bg-[#F5F5F1] text-stone-900">
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[#F5F5F1]" />
        <div className="absolute -top-32 right-0 h-[min(50vw,420px)] w-[min(50vw,420px)] rounded-full bg-amber-100/30 blur-3xl" />
        <div className="absolute -bottom-24 left-0 h-72 w-72 rounded-full bg-stone-200/40 blur-3xl" />
      </div>
      <Navbar />

      <div className="relative z-10 w-full flex-1 pt-20">
        <div className="b2b-hero relative overflow-hidden border-b border-stone-200/80 bg-[#F5F5F1] py-6 md:py-10">
          <div
            className="pointer-events-none absolute inset-0 [background-image:radial-gradient(circle_at_1px_1px,#d6d3d1_1px,transparent_0)] [background-size:28px_28px] opacity-40 [mask-image:linear-gradient(to_bottom,black,transparent_92%)]"
            aria-hidden
          />
          <div className="relative z-10 mx-auto flex w-full max-w-6xl flex-col items-center justify-center px-4 py-10 md:px-6 md:py-12">
            <motion.div
              className="w-full max-w-4xl text-center"
              initial="hidden"
              animate="visible"
              variants={heroContainer}
            >
              <motion.div variants={heroItem} className="flex justify-center">
                <span className="inline-flex items-center gap-2 rounded-full border border-stone-200/90 bg-white/90 px-4 py-2 text-sm font-medium text-stone-700 shadow-sm shadow-stone-900/5 backdrop-blur-sm">
                  <Sparkles className="h-4 w-4 text-amber-600/90" strokeWidth={1.75} aria-hidden />
                  Subscription learning for organizations
                </span>
              </motion.div>
              <motion.h1
                variants={heroItem}
                className="mt-8 flex flex-wrap items-end justify-center gap-x-4 gap-y-1.5 text-4xl font-bold leading-[1.12] tracking-tight text-stone-900 sm:gap-x-5 sm:text-5xl md:gap-x-6 md:text-[3.25rem] md:leading-[1.1]"
              >
                <span>Level up your team with</span>
                <span className="inline-flex max-w-full items-end justify-center">
                  <img                     src="/images/logo-v2.png"
                    alt="inaraX"
                    width={400}
                    height={120}
                    className="h-[1.15em] w-auto max-w-[min(100%,24rem)] object-contain object-bottom"
                  />
                </span>
              </motion.h1>
              <motion.p variants={heroItem} className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-stone-600 md:text-lg">
                Your organization subscribes to inaraX; we build a course for your people. Employees learn in the app while
                your admins monitor progress, lesson completion, and quiz scores from a single dashboard.
              </motion.p>
              <motion.div variants={heroItem} className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <motion.div
                  className="inline-flex"
                  whileHover={reduce ? undefined : { scale: 1.02 }}
                  whileTap={reduce ? undefined : { scale: 0.98 }}
                >
                  <Link
                    href="/b2b/register"
                    className="inline-flex h-14 min-w-[min(100%,15rem)] cursor-pointer items-center justify-center gap-2 rounded-full border border-indigo-600 bg-indigo-600 px-8 text-base font-semibold !text-white shadow-md shadow-indigo-600/25 transition hover:bg-indigo-500"
                  >
                    <span>Register Workspace</span>
                    <ArrowRight className="h-4 w-4 shrink-0 !text-white" strokeWidth={2} aria-hidden />
                  </Link>
                </motion.div>
                <motion.div
                  className="inline-flex"
                  whileHover={reduce ? undefined : { scale: 1.02 }}
                  whileTap={reduce ? undefined : { scale: 0.98 }}
                >
                  <a
                    href={mailHref}
                    className="inline-flex h-14 min-w-[min(100%,15rem)] cursor-pointer items-center justify-center gap-2 rounded-full border border-stone-300 bg-white/90 px-8 text-base font-semibold text-stone-800 shadow-sm transition hover:bg-stone-100"
                  >
                    <span>Contact sales</span>
                  </a>
                </motion.div>
              </motion.div>
            </motion.div>

            <B2BHeroHubDiagram className="mt-12 md:mt-16" />
          </div>
        </div>

        {/* — How it works (after hero) — */}
        <Section
          className="border-t border-stone-200/80 bg-gradient-to-b from-amber-50/20 via-stone-50/80 to-white"
          id="how-it-works"
        >
          <B2BHowItWorks />
        </Section>

        {/* — Why choose — */}
        <Section className="border-t border-stone-200/80 bg-gradient-to-b from-stone-50/90 via-white to-amber-50/15">
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6, ease }}
          >
            <h2 className="text-center text-2xl font-black tracking-tight text-stone-900 md:text-3xl lg:text-4xl">
              Why partner with inaraX
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-base leading-relaxed text-stone-600 md:text-lg">
              A clear trade: you subscribe, we deliver a course, your people learn, and your organization can prove progress and
              quiz performance from one place.
            </p>
            <ul className="mx-auto mt-12 grid max-w-5xl gap-6 sm:grid-cols-2">
              {VALUE_PROPS.map((item) => (
                <motion.li
                  key={item.title}
                  className="relative overflow-hidden rounded-2xl border border-stone-200/80 bg-white p-7 pl-8 shadow-sm shadow-stone-200/50 ring-1 ring-stone-100/80 backdrop-blur-sm"
                  initial={reduce ? false : { opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, ease }}
                >
                  <div
                    aria-hidden
                    className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-stone-800 via-stone-600 to-amber-700/30"
                  />
                  <h3 className="text-lg font-bold text-stone-900 md:text-xl">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-stone-600 md:text-base">{item.body}</p>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </Section>

        {/* — FAQ — */}
        <Section className="border-t border-stone-200/80 bg-stone-50/50" id="faq">
          <h2 className="text-center text-2xl font-black tracking-tight text-stone-900 md:text-3xl lg:text-4xl">Questions we hear often</h2>
          <p className="mx-auto mt-4 max-w-2xl text-center text-stone-600">
            Straight answers on subscriptions, custom courses, dashboards, and how your org and users will experience inaraX.
          </p>
          <div className="mx-auto mt-10 max-w-3xl rounded-2xl border border-stone-200/90 bg-white px-5 py-1 shadow-sm md:px-8 md:py-2">
            {FAQS.map((item) => (
              <FaqItem key={item.q} {...item} />
            ))}
          </div>
        </Section>

        {/* — Contact CTA — */}
        <Section
          className="border-t border-stone-800/20 bg-gradient-to-b from-stone-900 to-stone-800 text-stone-100 scroll-mt-24"
          id="book-demo"
        >
          <h2 className="text-center text-2xl font-black tracking-tight text-white md:text-3xl lg:text-4xl">Let&apos;s talk</h2>
          <p className="mx-auto mt-4 max-w-xl text-center text-base text-stone-400 md:text-lg">
            Tell us your org, rough seat count, and the skills or outcomes you need the course to support. We&apos;ll map a
            subscription and delivery plan to match.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
            <a
              href={mailHref}
              className="inline-flex h-12 min-w-[220px] items-center justify-center gap-2 rounded-full bg-white px-8 text-sm font-bold text-stone-900 shadow-lg shadow-stone-950/20 transition-all hover:-translate-y-0.5 hover:bg-stone-100"
            >
              <Mail className="h-4 w-4" aria-hidden />
              Email {CONTACT_MAIL}
            </a>
            <Link
              to="/catalog"
              className="inline-flex h-12 min-w-[220px] items-center justify-center rounded-full border border-stone-500/80 bg-transparent px-8 text-sm font-bold text-stone-100 transition-all hover:-translate-y-0.5 hover:border-stone-300 hover:bg-white/5"
            >
              Browse courses
            </Link>
          </div>
        </Section>
      </div>

      <div className="w-full border-t border-stone-200/90 bg-stone-100/90">
        <Footer />
      </div>
    </main>
  );
}
